import React from 'react';

const PHASE_META = {
  uyanis: {
    label: 'Uyanış',
    emoji: '🌱',
    tone: '#166534',
    scene: 'linear-gradient(125deg, #fff0d3 0%, #e4f8df 50%, #c9f5df 100%)',
  },
  yeserme: {
    label: 'Yeşerme',
    emoji: '🌿',
    tone: '#166534',
    scene: 'linear-gradient(125deg, #fff0cf 0%, #dff6dc 50%, #bff0cf 100%)',
  },
  buyume: {
    label: 'Büyüme',
    emoji: '🌳',
    tone: '#14532d',
    scene: 'linear-gradient(125deg, #f9ebca 0%, #d3f0cf 50%, #a8e6bd 100%)',
  },
  ciceklenme: {
    label: 'Çiçeklenme',
    emoji: '🌸',
    tone: '#7c2d12',
    scene: 'linear-gradient(125deg, #ffe9c8 0%, #f9dce8 48%, #f7bee3 100%)',
  },
};

function toInt(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function normalizePhase(phaseRaw) {
  const value = String(phaseRaw || '').trim().toLowerCase();
  return PHASE_META[value] ? value : 'uyanis';
}

function getMonthLabel(monthKey) {
  if (!/^\d{4}-\d{2}$/.test(String(monthKey || ''))) {
    return '';
  }
  const [yy, mm] = String(monthKey).split('-').map(Number);
  const dt = new Date(yy, (mm || 1) - 1, 1);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
}

function getForestProgress(drops, target) {
  if (target <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((drops / target) * 100)));
}

function getForestGlyphs(phaseKey) {
  if (phaseKey === 'ciceklenme') return ['🌸', '🌳', '🌸', '🌿'];
  if (phaseKey === 'buyume') return ['🌳', '🌿', '🌳', '🌱'];
  if (phaseKey === 'yeserme') return ['🌿', '🌱', '🌿', '🌱'];
  return ['🌱', '🌱', '🌿', '🌱'];
}

function getPulseText(value, canContribute) {
  const count = (typeof value === 'number' || (typeof value === 'string' && value.trim())) ? Number(value) : NaN;
  if (!Number.isInteger(count) || count < 0) {
    return 'Günlük katılım bilgisi şu anda alınamıyor.';
  }
  if (count === 0) {
    return canContribute ? 'Bugünün ilk damlası henüz eklenmedi. İlk katkı sizden gelsin mi?' : 'Bugün henüz veli katılımı kaydı yok.';
  }
  return `Bugün ${count} veli çocuklarıyla küçük bir adım attı. Ormanımız hep birlikte büyüyor!`;
}

function ForestTree({ phaseKey }) {
  const flowering = phaseKey === 'ciceklenme';
  const canopy = flowering ? ['#f7a5cb', '#f58dbb', '#fac1db'] : ['#88d5a0', '#62c98e', '#a7e3ae'];
  return (
    <svg aria-hidden="true" viewBox="0 0 150 160" style={{ position:'absolute', right:-9, bottom:36, width:150, height:160, opacity:0.55, pointerEvents:'none' }}>
      <path d="M75 150V76m0 39-27-24m27 13 29-27" fill="none" stroke="#936c58" strokeWidth="13" strokeLinecap="round" />
      <circle cx="72" cy="66" r="37" fill={canopy[0]} />
      <circle cx="40" cy="80" r="27" fill={canopy[1]} />
      <circle cx="108" cy="76" r="31" fill={canopy[2]} />
      <circle cx="60" cy="38" r="24" fill={canopy[2]} />
      <circle cx="98" cy="39" r="26" fill={canopy[1]} />
      {flowering && [38, 66, 92, 117].map((x, i) => (
        <circle key={x} cx={x} cy={[72, 47, 88, 61][i]} r="5" fill="#ffe3f1" />
      ))}
    </svg>
  );
}

export default function FutureForestCard({
  forest,
  canContribute = true,
}) {
  const drops = toInt(forest?.drops);
  const target = toInt(forest?.target);
  const monthLabel = getMonthLabel(forest?.month_key);
  const phaseKey = normalizePhase(forest?.phase);
  const phaseMeta = PHASE_META[phaseKey] || PHASE_META.uyanis;
  const progressPct = getForestProgress(drops, target);
  const forestGlyphs = getForestGlyphs(phaseKey);

  return (
    <section className="student360-forest" aria-label="Gelecek Ormanı" style={{ background:'#fff', borderRadius:24, padding:'clamp(14px, 3vw, 20px)', boxShadow:'0 12px 30px rgba(29,65,107,0.09)', width:'100%', maxWidth:'100%', boxSizing:'border-box', overflow:'hidden' }}>
      <h2 style={{ margin:'0 0 8px', color:'#0f1e56', fontSize:'clamp(22px, 4vw, 26px)', fontWeight:900, lineHeight:1.2 }}>
        <span aria-hidden="true" style={{ marginRight:10 }}>🌳</span>Gelecek Ormanı
      </h2>
      <style>{`
        @keyframes kk-forest-float {
          0% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
          100% { transform: translateY(0); }
        }
        @keyframes kk-forest-grow {
          from { transform: scale(0.96); opacity: 0.85; }
          to { transform: scale(1); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) { .student360-forest * { animation: none !important; } }
      `}</style>

      <div
        style={{
          borderRadius: 22,
          border: '1px solid rgba(255,255,255,0.65)',
          background: phaseMeta.scene,
          padding: '12px 14px',
          marginBottom: 8,
          position: 'relative',
          overflow: 'hidden',
          animation: 'kk-forest-grow 380ms ease-out',
        }}
      >
        <ForestTree phaseKey={phaseKey} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap:'wrap', gap: 10, position:'relative' }}>
          <div>
            <div style={{ fontSize: 13, color: '#66564e', fontWeight: 700 }}>
              {monthLabel || 'Bu ay'}
            </div>
            <div style={{ fontSize: 16, color: '#0f1e56', marginTop: 10, fontWeight: 800 }}>
              Sınıfımız bu ay
            </div>
            <div style={{ fontSize: 'clamp(26px, 6vw, 34px)', fontWeight: 900, color: '#0f1e56', lineHeight: 1.12, marginTop: 2 }}>
              💧 {drops} damla topladı
            </div>
            <div style={{ fontSize: 15, color: '#52617c', marginTop: 6 }}>
              Ormanımız büyüyor.
            </div>
          </div>

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.55)',
              color: phaseMeta.tone,
              border: '1px solid rgba(22, 101, 52, 0.18)',
              padding: '7px 11px',
              fontSize: 13,
              fontWeight: 800,
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ animation: 'kk-forest-float 2.2s ease-in-out infinite' }}>{phaseMeta.emoji}</span>
            <span>Evre: {phaseMeta.label}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 8, fontSize: 25, opacity: 0.92, position:'relative' }}>
          {forestGlyphs.map((glyph, idx) => (
            <span key={`${glyph}-${idx}`} style={{ lineHeight: 1 }}>{glyph}</span>
          ))}
        </div>

        <div style={{ marginTop: 10, position:'relative' }}>
          <div style={{ fontSize: 13, color: '#065f46', fontWeight: 900, marginBottom: 7 }}>
            Hedef: {target > 0 ? `${target} damla` : 'hazırlanıyor'}
          </div>
          <div style={{ height: 13, background: 'rgba(255,255,255,0.78)', borderRadius: 999, overflow: 'hidden' }}>
            <div
              style={{
                width: target > 0 ? `${progressPct}%` : '0%',
                height: '100%',
                borderRadius: 999,
                background: 'linear-gradient(90deg, #16a34a 0%, #15803d 100%)',
                transition: 'width 260ms ease',
              }}
            />
          </div>
          <div style={{ fontSize: 13, color: '#315877', marginTop: 7, fontWeight:700 }}>
            {target > 0 ? `${drops} / ${target} damla` : `${drops} damla`}
          </div>
        </div>
      </div>

      <div
        style={{
          border: '1px solid #dbeafe',
          borderRadius: 16,
          background: '#f8fbff',
          padding: '10px 12px',
          marginBottom: 8,
        }}
      >
        <div style={{ fontSize: 16, color: '#1e3a8a', fontWeight: 900, marginBottom: 5 }}>Günlük sınıf nabzı</div>
        <div style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
          {getPulseText(forest?.today_active_parents_count, canContribute)}
        </div>
      </div>

    </section>
  );
}
